import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Flame, Trophy, Award, MessageSquare, Package, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BADGE_LABELS = {
  newcomer: 'Nowicjusz',
  first_deal: 'Pierwszy deal',
  commenter: 'Komentator',
  hot_hunter: 'Łowca okazji',
};

export default function ProfilePage() {
  const { user, refreshUser, authHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/user/profile`, { headers: authHeaders() });
        setProfile(res.data);
      } catch {
        toast.error('Nie udało się załadować profilu');
      }
      setLoading(false);
    };
    fetchProfile();
  }, [authHeaders]);

  if (loading) return (
    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
  );

  if (!profile) return null;

  const { stats, zorki_history } = profile;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="profile-page">
      {/* Header */}
      <div className="bg-card border border-border/40 p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
            <span className="font-heading text-2xl font-bold text-primary">
              {user?.nick?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-white">{user?.nick}</h1>
            <p className="text-sm text-muted-foreground">{user?.email || user?.phone || 'Łowca okazji'}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="profile-stats">
        <div className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
          <Flame className="w-6 h-6 text-zorki mb-1" />
          <span className="font-heading text-2xl font-bold text-zorki">{stats.zorki}</span>
          <span className="text-xs text-muted-foreground">Zorków</span>
        </div>
        <div className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
          <Package className="w-6 h-6 text-primary mb-1" />
          <span className="font-heading text-2xl font-bold text-white">{stats.deals_count}</span>
          <span className="text-xs text-muted-foreground">Okazji</span>
        </div>
        <div className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
          <MessageSquare className="w-6 h-6 text-primary mb-1" />
          <span className="font-heading text-2xl font-bold text-white">{stats.comments_count}</span>
          <span className="text-xs text-muted-foreground">Komentarzy</span>
        </div>
        <div className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
          <Trophy className="w-6 h-6 text-primary mb-1" />
          <span className="font-heading text-2xl font-bold text-white">{stats.streak}</span>
          <span className="text-xs text-muted-foreground">Dni streak</span>
        </div>
      </div>

      {/* Badges */}
      <div className="bg-card border border-border/40 p-4 mb-6">
        <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Award className="w-5 h-5 text-primary" /> Odznaki
        </h2>
        <div className="flex flex-wrap gap-2">
          {stats.badges.map(badge => (
            <span
              key={badge}
              className="px-3 py-1 text-xs font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30"
              data-testid={`badge-${badge}`}
            >
              {BADGE_LABELS[badge] || badge}
            </span>
          ))}
        </div>
      </div>

      {/* Zorki History */}
      <div className="bg-card border border-border/40 p-4" data-testid="zorki-history">
        <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Flame className="w-5 h-5 text-zorki" /> Historia Zorków
        </h2>
        <div className="space-y-2">
          {zorki_history.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak transakcji</p>
          ) : (
            zorki_history.map(tx => (
              <div key={tx.id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                <div>
                  <span className="text-sm text-white">{tx.reason}</span>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Clock className="w-3 h-3" />
                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString('pl-PL') : ''}
                  </div>
                </div>
                <span className={`font-heading font-bold ${tx.amount > 0 ? 'text-zorki' : 'text-destructive'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
